import React, { useState, useRef, useEffect } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';

interface ZipCodeAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  label?: string;
}

// Sample zip codes for major US cities - in a real app, this would come from an API
const POPULAR_ZIP_CODES = [
  { zip: '10001', city: 'New York', state: 'NY' },
  { zip: '90210', city: 'Beverly Hills', state: 'CA' },
  { zip: '60601', city: 'Chicago', state: 'IL' },
  { zip: '77001', city: 'Houston', state: 'TX' },
  { zip: '85001', city: 'Phoenix', state: 'AZ' },
  { zip: '19101', city: 'Philadelphia', state: 'PA' },
  { zip: '78701', city: 'Austin', state: 'TX' },
  { zip: '33101', city: 'Miami', state: 'FL' },
  { zip: '98101', city: 'Seattle', state: 'WA' },
  { zip: '02101', city: 'Boston', state: 'MA' },
  { zip: '30301', city: 'Atlanta', state: 'GA' },
  { zip: '80201', city: 'Denver', state: 'CO' },
  { zip: '20001', city: 'Washington', state: 'DC' },
  { zip: '97201', city: 'Portland', state: 'OR' },
  { zip: '55401', city: 'Minneapolis', state: 'MN' },
  { zip: '63101', city: 'St. Louis', state: 'MO' },
  { zip: '28201', city: 'Charlotte', state: 'NC' },
  { zip: '32801', city: 'Orlando', state: 'FL' },
  { zip: '89101', city: 'Las Vegas', state: 'NV' },
  { zip: '94101', city: 'San Francisco', state: 'CA' },
];

const ZipCodeAutocomplete: React.FC<ZipCodeAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Enter zip code",
  className = "",
  id,
  label
}) => {
  console.log('Rendering ZipCodeAutocomplete component');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<typeof POPULAR_ZIP_CODES>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (value.length >= 2) {
      const filtered = POPULAR_ZIP_CODES.filter(item =>
        item.zip.startsWith(value) ||
        item.city.toLowerCase().includes(value.toLowerCase()) ||
        item.state.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8); // Limit to 8 suggestions
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (inputValue: string) => {
    // Only allow digits and limit to 10 characters (for ZIP+4 format)
    const cleanValue = inputValue.replace(/\D/g, '').slice(0, 10);
    
    // Format as ZIP+4 if applicable
    let formattedValue = cleanValue;
    if (cleanValue.length > 5) {
      formattedValue = `${cleanValue.slice(0, 5)}-${cleanValue.slice(5)}`;
    }
    
    onChange(formattedValue);
    setIsOpen(true);
  };

  const handleSuggestionClick = (suggestion: typeof POPULAR_ZIP_CODES[0]) => {
    console.log('Selected zip code suggestion:', suggestion);
    onChange(suggestion.zip);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    if (filteredSuggestions.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPinIcon className="w-5 h-5 text-slate-400" />
        </div>
        <input
          type="text"
          id={id}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className={`w-full pl-10 pr-3 py-3 bg-slate-700 text-slate-100 border border-slate-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
          autoComplete="postal-code"
        />
      </div>

      {/* Dropdown Suggestions */}
      {isOpen && filteredSuggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.map((suggestion) => (
            <button
              key={suggestion.zip}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full text-left px-4 py-3 hover:bg-slate-600 focus:bg-slate-600 focus:outline-none border-b border-slate-600 last:border-b-0"
            >
              <div className="flex justify-between items-center">
                <span className="text-slate-100 font-medium">{suggestion.zip}</span>
                <span className="text-slate-300 text-sm">{suggestion.city}, {suggestion.state}</span>
              </div>
            </button>
          ))}
          {value.length >= 5 && !filteredSuggestions.some(s => s.zip === value) && (
            <div className="px-4 py-3 text-slate-400 text-sm border-t border-slate-600">
              Press Enter to use "{value}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ZipCodeAutocomplete; 